<template>
  <div>
    <b-card
      v-if="errors && errors.length > 0"
      variant="danger"
      class="mb-6"
    >
      <ul class="bookapp-errors">
        <li v-for="error in errors" :key="error">
          {{ error }}
        </li>
      </ul>
    </b-card>

    <div v-if="processing" class="flex flex-col items-center justify-center h-screen w-screen inset-0 fixed bg-secondary/40 backdrop-blur z-50">
      <b-spinner size="h-8 w-8"/>
      <span class="text-secondary mt-5 ">{{ $t('components.payments.paymentForm.processing') }}...</span>
    </div>

    <div v-if="paymentWaiting && !processing">
      <b-alert :title="$t('components.payments.paymentForm.followSteps')" variant="warning">
        <b-button size="sm" variant="action" @click="cancelWaiting">
          « {{ $t('components.payments.paymentForm.backToOptions') }}
        </b-button>
      </b-alert>
    </div>

    <div v-show="!paymentWaiting && !processing" class="mb-6">
      <div v-for="method in methods" :key="`selection` + method.code" class="rounded bg-primary mb-2">
        <button class="flex items-center justify-between p-3 focus:bg-action/30 w-full" @click="selectPaymentMethod(method)">
          <div class="flex items-center space-x-4  rtl:space-x-reverse">
            <div class="h-6 w-6 rounded-full border-2 border-action flex items-center justify-center">
              <div v-if="method === selectedPaymentMethod" class="h-3 w-3 rounded-full bg-action" />
            </div>

            <component :is="paymentMethodSelectionComponent(method)" :key="method.data" class="text-secondary" />
          </div>

          <span v-if="(method === selectedPaymentMethod)" class="text-secondary font-medium">
            <template v-if="amount">
              {{ method.amountLabel || amountLabel }}
              <template v-if="method.minimumPayment">
                <br><small>({{ $t('components.payments.paymentForm.minimumAmount', { amount: method.minimumPayment.amountLabel }) }})</small>
              </template>
            </template>
          </span>
        </button>
        <div v-if="(method === selectedPaymentMethod) && paymentMethodComponent(selectedPaymentMethod)" class="p-3">
          <client-only>
            <component
              :is="paymentMethodComponent(selectedPaymentMethod)"
              :key="selectedPaymentMethod.data"
              v-bind="paymentMethodComponentProps(selectedPaymentMethod)"
              class="text-secondary text-sm"
              @callback="paymentCallback"
            />
          </client-only>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Buffer } from 'buffer';
import PaymentIntegrations from '@/components/payments/integrations'

const route = useRoute()
const router = useRouter()

const { $api } = useNuxtApp()

interface Props {
  methods: any[]
  label: string
  amount: number
  amountLabel: string
  confirmButtonTitle: string
  action: Function
  disabled: boolean
  fallbackRoute: Function
  processing?: boolean
  customer?: any
  intent?: string
}

const props = withDefaults(defineProps<Props>(), {
  processing: false,
  customer: null,
  intent: 'charge'
})

const emit = defineEmits(['callback'])

const errors = ref<string[]>([])
const selectedPaymentMethod = ref()
const selectedPaymentIntegration = ref()
const paymentInterval = ref()
const paymentWaiting = ref(false)
const callbacksExternalPayment = ref()

watch(
  () => props.methods,
  () => {
    if (selectedPaymentMethod.value && !props.methods.includes(selectedPaymentMethod.value)) {
      let found = false

      for (let i = 0; i < props.methods.length; i++) {
        const method = props.methods[i]

        if (method.code === selectedPaymentMethod.value.code) {
          selectPaymentMethod(method)

          found = true
          break
        }
      }

      if (!found) { selectPaymentMethod(props.methods[0]) }
    }
  }
)


onMounted(() => {
  const query = route.query

  if (query.paymentCallback) {
    const paymentCallback = JSON.parse(Buffer.from(query.paymentCallback.toString(), 'base64').toString())

    const integrationGuid = paymentCallback.integration
    if (integrationGuid) {
      if (props.methods) {
        for (let index = 0; index < props.methods.length; index++) {
          const method = props.methods[index]

          if (method.type === 'integration' && method.integration.guid === integrationGuid) {
            selectPaymentMethod(method)

            router.replace({ query: { ...route.query, paymentCallback: null } })

            emit('callback', method, paymentCallback.response)
          }
        }
      }
    }
  }

  if (!selectedPaymentMethod.value) { selectPaymentMethod(props.methods[0]) }
})


const paymentMethodComponent = (method) => {
  if (method.type === 'integration') {
    selectedPaymentIntegration.value = PaymentIntegrations[method.integration.guid]

    if (selectedPaymentIntegration.value) {
      return selectedPaymentIntegration.value.component
    }
  }

  return null
}


const paymentMethodSelectionComponent = (method) => {
  if (method.type === 'integration') {
    const selectedPaymentIntegration = PaymentIntegrations[method.integration.guid]

    if (selectedPaymentIntegration) {
      return selectedPaymentIntegration.selection
    }
  }

  return null
}


const paymentMethodComponentProps = (method) => {
  if (method.type === 'integration') {
    selectedPaymentIntegration.value = PaymentIntegrations[method.integration.guid]

    const actionFunction = props.action
    const confirmFunction = props.confirm

    const that = this

    return {
      label: props.label,
      amount: props.amount,
      confirmButtonTitle: props.confirmButtonTitle,
      integration: method.integration,
      async callbackUrl () {
        const _fallbackRoute = props.fallbackRoute(method)

        const cache = await $api.location.setState({ data: _fallbackRoute })

        const callbackUrl = window.location.protocol + '//' + window.location.host + router.resolve({ path: '/payment/callback', query: { integration: method.integration.guid, state: Buffer.from(JSON.stringify({ key: cache.key, auth: cache.auth })).toString('base64') } }).href

        return callbackUrl
      },
      disabled: props.disabled,
      customer: props.customer,
      intent: props.intent,
      action (params) {
        return actionFunction(method, params)
      },
      confirm (params) {
        return confirmFunction(method, params)
      },
      processExternal (url, windowOptions, callback) {
        paymentWaiting.value = true

        const win = window.open(url, '', windowOptions)

        if (callbacksExternalPayment.value) {
          callbacksExternalPayment.value.cancel()
        }

        callbacksExternalPayment.value = window.postRobot.on('payment.external.callback', { window: win }, function (event) {
          const data = event.data

          paymentWaiting.value = false

          callback(data)

          return {
            success: true
          }
        })
      }
    }
  }
}

const selectPaymentMethod = (method) => {
  if (selectedPaymentMethod.value === method) { return }

  if (selectedPaymentMethod.value) {
    selectedPaymentMethod.value = null
  }

  if (method) {
    nextTick(() => {
      selectedPaymentMethod.value = method
    })
  }
}

const paymentCallback = (result) => {
  emit('callback', selectedPaymentMethod.value, result)
}

const cancelWaiting = () => {
  clearInterval(paymentInterval.value)

  paymentWaiting.value = false
}
</script>

<style scoped>

.payment-method--component {
  background-color: var(--bookapp-content-tint1-color) !important;
}

.progress {
  height: 5em;
}

.progress .progress-bar {
  width:100%;
}

</style>
